import React, { useState } from 'react';
import {
  Users, Search, Plus, Edit2, Trash2, Mail, Phone, Package,
  DollarSign, Eye, X, Save, MoreVertical
} from 'lucide-react';

// Mock customers data
const mockCustomers = [
  {
    id: 1,
    name: 'Marcus Johnson',
    code: 'MJ-4521',
    email: 'marcus@email.com',
    phone: '+1 (876) 555-0123',
    totalShipments: 47,
    totalSpent: 245000,
    status: 'active',
    joinDate: 'Mar 2024'
  },
  {
    id: 2,
    name: 'Sarah Williams',
    code: 'SW-3892',
    email: 'sarah@email.com',
    phone: '+1 (876) 555-0456',
    totalShipments: 38,
    totalSpent: 198000,
    status: 'active',
    joinDate: 'Apr 2024'
  },
  {
    id: 3,
    name: 'James Brown',
    code: 'JB-2741',
    email: 'james@email.com',
    phone: '+1 (876) 555-0789',
    totalShipments: 31,
    totalSpent: 167000,
    status: 'active',
    joinDate: 'May 2024'
  },
  {
    id: 4,
    name: 'Emily Davis',
    code: 'ED-1523',
    email: 'emily@email.com',
    phone: '+1 (876) 555-0321',
    totalShipments: 28,
    totalSpent: 142000,
    status: 'active',
    joinDate: 'Jun 2024'
  },
  {
    id: 5,
    name: 'Michael Chen',
    code: 'MC-4102',
    email: 'michael@email.com',
    phone: '+1 (876) 555-0654',
    totalShipments: 24,
    totalSpent: 128000,
    status: 'inactive',
    joinDate: 'Jul 2024'
  },
];

const customerStats = {
  total: 5,
  active: 4,
  totalShipments: 168,
  totalRevenue: 880000
};

export default function TenantCustomers() {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const filteredCustomers = mockCustomers.filter(c =>
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openCustomerDetail = (customer) => {
    setSelectedCustomer(customer);
    setShowDetailModal(true);
  };

  const openEditModal = (customer) => {
    setEditingCustomer(customer);
    setShowAddModal(true);
  };

  return (
    <div className="tenant-customers-page" data-testid="tenant-customers">
      {/* Summary Stats */}
      <div className="customers-summary-grid">
        <div className="summary-stat-card">
          <Users size={24} />
          <div>
            <span className="stat-value">{customerStats.total}</span>
            <span className="stat-label">Total Customers</span>
          </div>
        </div>
        <div className="summary-stat-card active">
          <Users size={24} />
          <div>
            <span className="stat-value">{customerStats.active}</span>
            <span className="stat-label">Active</span>
          </div>
        </div>
        <div className="summary-stat-card">
          <Package size={24} />
          <div>
            <span className="stat-value">{customerStats.totalShipments}</span>
            <span className="stat-label">Total Shipments</span>
          </div>
        </div>
        <div className="summary-stat-card">
          <DollarSign size={24} />
          <div>
            <span className="stat-value">JMD {(customerStats.totalRevenue / 1000).toFixed(0)}K</span>
            <span className="stat-label">Total Revenue</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="customers-toolbar">
        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search customers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="customer-search"
          />
        </div>
        
        <button 
          className="btn btn-primary"
          onClick={() => { setEditingCustomer(null); setShowAddModal(true); }}
          data-testid="add-customer-btn"
        >
          <Plus size={14} /> Add Customer
        </button>
      </div>

      {/* Customers Grid */}
      <div className="customers-grid">
        {filteredCustomers.map((customer) => (
          <div key={customer.id} className="customer-card" data-testid={`customer-${customer.id}`}>
            <div className="customer-card-header">
              <div className="customer-avatar">
                {customer.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div className="customer-info">
                <span className="customer-name">{customer.name}</span>
                <span className="customer-code">{customer.code}</span>
              </div>
              <div className={`status-indicator ${customer.status}`}></div>
            </div>

            <div className="customer-card-body">
              <div className="customer-contact">
                <div className="contact-item">
                  <Mail size={14} />
                  <span>{customer.email}</span>
                </div>
                <div className="contact-item">
                  <Phone size={14} />
                  <span>{customer.phone}</span>
                </div>
              </div>

              <div className="customer-stats">
                <div className="stat">
                  <Package size={14} />
                  <span>{customer.totalShipments} shipments</span>
                </div>
                <div className="stat">
                  <DollarSign size={14} />
                  <span>JMD {(customer.totalSpent / 1000).toFixed(0)}K spent</span>
                </div>
              </div>
            </div>

            <div className="customer-card-footer">
              <span className="join-date">Since {customer.joinDate}</span>
              <div className="customer-actions">
                <button 
                  className="action-btn"
                  onClick={() => openCustomerDetail(customer)}
                  data-testid={`view-${customer.id}`}
                >
                  <Eye size={14} />
                </button>
                <button 
                  className="action-btn"
                  onClick={() => openEditModal(customer)}
                  data-testid={`edit-${customer.id}`}
                >
                  <Edit2 size={14} />
                </button>
                <button className="action-btn delete" data-testid={`delete-${customer.id}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Add/Edit Customer Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div 
            className="modal-content customer-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="customer-modal"
          >
            <div className="modal-header">
              <h3>{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</h3>
              <button className="close-btn" onClick={() => setShowAddModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="form-row">
                <div className="form-group">
                  <label>Full Name</label>
                  <input
                    type="text"
                    placeholder="Enter full name"
                    defaultValue={editingCustomer?.name || ''}
                    data-testid="customer-name-input"
                  />
                </div>
                <div className="form-group">
                  <label>Customer Code</label>
                  <input
                    type="text"
                    placeholder="Auto-generated"
                    defaultValue={editingCustomer?.code || ''}
                    disabled={!editingCustomer}
                    data-testid="customer-code-input"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Email Address</label>
                <input
                  type="email"
                  placeholder="customer@email.com"
                  defaultValue={editingCustomer?.email || ''}
                  data-testid="customer-email-input"
                />
              </div>

              <div className="form-group">
                <label>Phone Number</label>
                <input
                  type="tel"
                  placeholder="+1 (876) 555-0000"
                  defaultValue={editingCustomer?.phone || ''}
                  data-testid="customer-phone-input"
                />
              </div>

              {editingCustomer && (
                <div className="form-group">
                  <label>Status</label>
                  <select defaultValue={editingCustomer?.status || 'active'} data-testid="customer-status">
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddModal(false)}>
                Cancel
              </button>
              <button className="btn btn-primary" data-testid="save-customer-btn">
                <Save size={14} /> {editingCustomer ? 'Save Changes' : 'Add Customer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Detail Modal */}
      {showDetailModal && selectedCustomer && (
        <div className="modal-overlay" onClick={() => setShowDetailModal(false)}>
          <div 
            className="modal-content customer-detail-modal"
            onClick={(e) => e.stopPropagation()}
            data-testid="customer-detail-modal"
          >
            <div className="modal-header">
              <div className="customer-header-info">
                <div className="customer-avatar large">
                  {selectedCustomer.name.split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <h3>{selectedCustomer.name}</h3>
                  <span className="customer-code">{selectedCustomer.code}</span>
                </div>
              </div>
              <button className="close-btn" onClick={() => setShowDetailModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="modal-body">
              <div className="customer-detail-grid">
                <div className="detail-card">
                  <h4>Contact Information</h4>
                  <div className="detail-row">
                    <Mail size={16} />
                    <span>{selectedCustomer.email}</span>
                  </div>
                  <div className="detail-row">
                    <Phone size={16} />
                    <span>{selectedCustomer.phone}</span>
                  </div>
                </div>

                <div className="detail-card">
                  <h4>Activity Summary</h4>
                  <div className="activity-stats">
                    <div className="activity-stat">
                      <span className="stat-value">{selectedCustomer.totalShipments}</span>
                      <span className="stat-label">Shipments</span>
                    </div>
                    <div className="activity-stat">
                      <span className="stat-value">JMD {(selectedCustomer.totalSpent / 1000).toFixed(0)}K</span>
                      <span className="stat-label">Total Spent</span>
                    </div>
                    <div className="activity-stat">
                      <span className={`stat-value ${selectedCustomer.status}`}>{selectedCustomer.status}</span>
                      <span className="stat-label">Status</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="recent-activity">
                <h4>Recent Shipments</h4>
                <div className="activity-list">
                  <div className="activity-item">
                    <Package size={14} />
                    <span>FS-2025-4521 - In Transit</span>
                    <span className="activity-date">Dec 15</span>
                  </div>
                  <div className="activity-item">
                    <Package size={14} />
                    <span>FS-2025-4498 - Delivered</span>
                    <span className="activity-date">Dec 10</span>
                  </div>
                  <div className="activity-item">
                    <Package size={14} />
                    <span>FS-2025-4476 - Delivered</span>
                    <span className="activity-date">Dec 5</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDetailModal(false)}>
                Close
              </button>
              <button className="btn btn-primary" data-testid="view-all-shipments">
                View All Shipments
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
